// src/features/media/components/MediaGrid.tsx

import { useRef, useCallback, useMemo, useState, useEffect, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Box, Checkbox, Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  IconVideo,
  IconChevronUp,
  IconChevronDown,
  IconEdit,
  IconCode,
  IconDownload,
  IconPlayerPlay,
  IconPlayerPause,
  IconPlaylistAdd,
  IconTrash,
} from '@tabler/icons-react';
import { MediaGridRow } from './MediaGridRow';
import { useSelection } from '@/shared/contexts/SelectionContext';
import {
  ROW_HEIGHT,
  VIDEO_GRID_MAX_VIEWPORT_HEIGHT_PX,
  VIDEO_GRID_MAX_VIRTUAL_ROWS,
  VIRTUALIZER_OVERSCAN,
} from '@/lib/constants/media';
import type { VideoGridRow, VideoSortKey, SortDirection } from '@/lib/types/Media';
import { JobProgressUtils, type MediaJobProgress } from '@/shared/utils/jobProgress';
import { isPerfDebugEnabled, recordDuration, recordSample } from '@/shared/utils/perf';
import styles from './MediaGrid.module.css';

interface MediaGridProps {
  count: number;
  allIds: number[];
  getRow: (index: number) => VideoGridRow | null;
  /** mediaId → per-phase active job percents; kept outside the engine's row cache so 5s job polls don't invalidate it. */
  processingProgress?: Map<number, MediaJobProgress>;
  sortKey: VideoSortKey;
  sortDirection: SortDirection;
  onSort: (key: VideoSortKey) => void;
  onPublish?: (video: VideoGridRow) => void;
  onAddToPlaylist?: (video: VideoGridRow) => void;
  onActivateToggle?: (video: VideoGridRow) => void;
  onDelete?: (video: VideoGridRow) => void;
  initialScrollTop?: number;
  onScrollTopChange?: (scrollTop: number) => void;
  onFirstVisibleRowsPaint?: () => void;
  onPrefetchDetail?: (video: VideoGridRow) => void;
}

interface HeaderColumn {
  key: string;
  label: string;
  sortable: boolean;
  sortKey?: VideoSortKey;
}

const HEADER_COLUMNS: HeaderColumn[] = [
  { key: 'checkbox', label: '', sortable: false },
  { key: 'thumbnail', label: '', sortable: false },
  { key: 'title', label: 'Title', sortable: true, sortKey: 'title' },
  { key: 'status', label: 'Status', sortable: true, sortKey: 'status' },
  { key: 'published', label: 'Published', sortable: true, sortKey: 'published' },
  { key: 'updated', label: 'Updated', sortable: true, sortKey: 'updated' },
  { key: 'actions', label: '', sortable: false },
];

export function MediaGrid({
  count,
  allIds,
  getRow,
  processingProgress,
  sortKey,
  sortDirection,
  onSort,
  onPublish,
  onAddToPlaylist,
  onActivateToggle,
  onDelete,
  initialScrollTop = 0,
  onScrollTopChange,
  onFirstVisibleRowsPaint,
  onPrefetchDetail,
}: MediaGridProps) {
  const renderStartRef = useRef(performance.now());
  renderStartRef.current = performance.now();
  const perfDebugEnabled = useRef(isPerfDebugEnabled()).current;

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const didRestoreScrollRef = useRef(false);
  const lastPaintTokenRef = useRef<string | null>(null);
  const didSampleViewportRef = useRef(false);
  const didSampleVirtualRowsRef = useRef(false);
  const didSampleDomRowsRef = useRef(false);
  const didReportVirtualRowsViolationRef = useRef(false);
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') {
      return 720;
    }

    const estimated = Math.max(0, window.innerHeight - 220);
    return Math.min(estimated, VIDEO_GRID_MAX_VIEWPORT_HEIGHT_PX);
  });
  const navigate = useNavigate();
  const { selectedIds, isSelected, selectOnly, toggle, toggleRange, addToSelection, removeFromSelection } = useSelection();

  // All video IDs for range selection — ref ensures handleRangeSelect never changes
  const allIdsRef = useRef<number[]>([]);
  allIdsRef.current = allIds;

  const virtualizer = useVirtualizer({
    count,
    // Seed the virtualizer's internal offset with the restored scroll position.
    // Its attach step syncs the element to that internal offset, so without this
    // a re-attach (e.g. StrictMode's dev double-mount) scrolls back to 0 and
    // undoes the scroll restore below.
    initialOffset: initialScrollTop,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT.comfortable,
    overscan: VIRTUALIZER_OVERSCAN,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const renderedVirtualRows = virtualRows.length > VIDEO_GRID_MAX_VIRTUAL_ROWS
    ? virtualRows.slice(0, VIDEO_GRID_MAX_VIRTUAL_ROWS)
    : virtualRows;

  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    const headerEl = headerRef.current;
    if (!containerEl || !headerEl || typeof ResizeObserver === 'undefined') return;

    const updateViewportHeight = () => {
      const measured = Math.max(0, containerEl.clientHeight - headerEl.offsetHeight);
      const fallback = typeof window !== 'undefined'
        ? Math.max(0, window.innerHeight - headerEl.offsetHeight - 140)
        : measured;
      const bounded = Math.min(
        measured > 0 ? measured : fallback,
        VIDEO_GRID_MAX_VIEWPORT_HEIGHT_PX
      );
      setViewportHeight((prev) => (Math.abs(prev - bounded) < 1 ? prev : bounded));
    };

    updateViewportHeight();

    const observer = new ResizeObserver(() => updateViewportHeight());
    observer.observe(containerEl);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || didRestoreScrollRef.current) return;

    if (initialScrollTop > 0) {
      scrollEl.scrollTop = initialScrollTop;
      virtualizer.scrollToOffset(initialScrollTop);
    }

    didRestoreScrollRef.current = true;
  }, [initialScrollTop, virtualizer]);

  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || !onScrollTopChange) return;

    const handleScroll = () => onScrollTopChange(scrollEl.scrollTop);
    handleScroll();
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [onScrollTopChange]);

  useEffect(() => {
    if (!perfDebugEnabled) return;
    recordDuration('videos.grid.render', performance.now() - renderStartRef.current, {
      count,
      virtualRows: renderedVirtualRows.length,
      viewportHeight,
    });
  }, [count, renderedVirtualRows.length, viewportHeight, perfDebugEnabled]);

  useEffect(() => {
    if (!perfDebugEnabled) return;
    recordSample('videos.virtual.rows.count', renderedVirtualRows.length, {
      count,
    });
  }, [renderedVirtualRows.length, count, perfDebugEnabled]);

  useEffect(() => {
    if (!perfDebugEnabled) return;
    const domRows = parentRef.current?.querySelectorAll('[data-media-grid-row="true"]').length ?? 0;
    recordSample('videos.dom.rows.count', domRows, { count });
  }, [renderedVirtualRows.length, count, perfDebugEnabled]);

  useEffect(() => {
    if (!perfDebugEnabled) return;
    if (viewportHeight <= 0 || didSampleViewportRef.current) return;
    didSampleViewportRef.current = true;
    recordSample('videos.grid.viewport.height', viewportHeight, {
      count,
      debugOnce: true,
    });
  }, [viewportHeight, count, perfDebugEnabled]);

  useEffect(() => {
    if (!perfDebugEnabled) return;
    if (renderedVirtualRows.length <= 0 || didSampleVirtualRowsRef.current) return;
    didSampleVirtualRowsRef.current = true;
    recordSample('videos.virtual.rows.count', renderedVirtualRows.length, {
      count,
      debugOnce: true,
    });
  }, [renderedVirtualRows.length, count, perfDebugEnabled]);

  useEffect(() => {
    if (!perfDebugEnabled) return;
    if (didSampleDomRowsRef.current) return;
    const domRows = parentRef.current?.querySelectorAll('[data-media-grid-row="true"]').length ?? 0;
    if (domRows <= 0) return;
    didSampleDomRowsRef.current = true;
    recordSample('videos.dom.rows.count', domRows, {
      count,
      debugOnce: true,
    });
  }, [renderedVirtualRows.length, count, perfDebugEnabled]);

  useEffect(() => {
    if (virtualRows.length <= VIDEO_GRID_MAX_VIRTUAL_ROWS) {
      didReportVirtualRowsViolationRef.current = false;
      return;
    }

    if (didReportVirtualRowsViolationRef.current) return;
    didReportVirtualRowsViolationRef.current = true;

    recordSample('videos.virtual.rows.count', virtualRows.length, {
      count,
      limit: VIDEO_GRID_MAX_VIRTUAL_ROWS,
      violation: true,
      viewportHeight,
    });

    console.error('[MediaGrid] Virtualization containment invariant violated', {
      virtualRows: virtualRows.length,
      limit: VIDEO_GRID_MAX_VIRTUAL_ROWS,
      viewportHeight,
      totalRows: count,
    });
  }, [virtualRows.length, viewportHeight, count]);

  useEffect(() => {
    if (count === 0 || renderedVirtualRows.length === 0) return;
    const paintToken = `${sortKey}:${sortDirection}:${count}`;
    if (lastPaintTokenRef.current === paintToken) return;
    lastPaintTokenRef.current = paintToken;
    onFirstVisibleRowsPaint?.();
  }, [count, renderedVirtualRows.length, sortKey, sortDirection, onFirstVisibleRowsPaint]);

  // Stable callback — reads from ref, so never changes identity
  const handleRangeSelect = useCallback(
    (id: number, index: number) => toggleRange(id, index, allIdsRef.current),
    [toggleRange]
  );

  // Client-side navigation for title links (single callback, not per-row)
  const handleNavigate = useCallback((href: string) => {
    navigate({ to: href });
  }, [navigate]);

  // --- Shared action menu state (single Menu instance for all rows) ---
  const [menuState, setMenuState] = useState<{
    opened: boolean;
    rowIndex: number | null;
  }>({ opened: false, rowIndex: null });

  const anchorRef = useRef<HTMLDivElement>(null);

  const handleMenuOpen = useCallback((index: number, buttonEl: HTMLElement) => {
    // Position the hidden anchor at the button's location
    const rect = buttonEl.getBoundingClientRect();
    if (anchorRef.current) {
      anchorRef.current.style.position = 'fixed';
      anchorRef.current.style.top = `${rect.top}px`;
      anchorRef.current.style.left = `${rect.left}px`;
      anchorRef.current.style.width = `${rect.width}px`;
      anchorRef.current.style.height = `${rect.height}px`;
    }
    setMenuState({ opened: true, rowIndex: index });
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuState(prev => ({ ...prev, opened: false }));
  }, []);

  // Close menu on scroll (the anchor stays fixed while the row scrolls away)
  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || !menuState.opened) return;
    const onScroll = () => handleMenuClose();
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [menuState.opened, handleMenuClose]);

  // Get video for active menu row
  const activeVideo = menuState.rowIndex !== null ? getRow(menuState.rowIndex) : null;

  // Delete confirmation (moved from per-row to shared)
  const handleRowDelete = useCallback((video: VideoGridRow) => {
    modals.openConfirmModal({
      title: 'Delete Video',
      children: (
        <Text size="sm">
          Are you sure you want to delete &ldquo;{video.title}&rdquo;? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => onDelete?.(video),
    });
  }, [onDelete]);

  // Header checkbox state — scoped to the VISIBLE (filtered) rows so a
  // search/filter never causes select-all to silently drop selections made
  // under a previous search. Memoized: scroll re-renders must not pay O(n).
  const visibleSelectedCount = useMemo(() => {
    if (selectedIds.size === 0) return 0;
    let n = 0;
    for (const id of allIds) {
      if (selectedIds.has(id)) n++;
    }
    return n;
  }, [allIds, selectedIds]);

  const allSelected = count > 0 && visibleSelectedCount === count;
  const someSelected = visibleSelectedCount > 0 && visibleSelectedCount < count;

  const handleHeaderCheckbox = () => {
    if (allSelected) {
      removeFromSelection(allIdsRef.current);
    } else {
      addToSelection(allIdsRef.current);
    }
  };

  const handleHeaderClick = (column: HeaderColumn) => {
    if (column.sortable && column.sortKey) {
      onSort(column.sortKey);
    }
  };

  // Empty state
  if (count === 0) {
    return (
      <Box ref={containerRef} className={styles.gridContainer}>
        <Box className={styles.emptyState}>
          <IconVideo size={48} stroke={1.5} opacity={0.5} />
          <Text size="lg" mt="md" fw={500}>No videos found</Text>
          <Text size="sm" c="dimmed" mt="xs">
            Try adjusting your search or filters
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} className={styles.gridContainer}>
      {/* Header row using CSS Grid */}
      <div ref={headerRef} className={`${styles.header} ${styles.gridColumns}`}>
        {HEADER_COLUMNS.map((col) => {
          if (col.key === 'checkbox') {
            return (
              <div key={col.key} className={`${styles.headerCell} ${styles.cellCheckbox}`}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleHeaderCheckbox}
                  aria-label="Select all"
                />
              </div>
            );
          }

          const isActive = col.sortable && col.sortKey === sortKey;

          return (
            <div
              key={col.key}
              data-col={col.key}
              className={`${styles.headerCell} ${col.sortable ? styles.sortableHeader : ''}`}
              style={{
                justifyContent: col.key === 'published' || col.key === 'updated' ? 'flex-end' :
                               col.key === 'status' ? 'center' : 'flex-start',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => handleHeaderClick(col)}
            >
              {col.label}
              {col.sortable && (
                <span className={`${styles.sortIcon} ${isActive ? styles.sortIconActive : ''}`}>
                  {isActive && sortDirection === 'asc' ? (
                    <IconChevronUp size={14} />
                  ) : isActive && sortDirection === 'desc' ? (
                    <IconChevronDown size={14} />
                  ) : (
                    <IconChevronDown size={14} style={{ opacity: 0.3 }} />
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Scrollable body with virtualization */}
      <div
        ref={parentRef}
        className={styles.body}
        style={viewportHeight > 0 ? { height: `${viewportHeight}px` } : undefined}
      >
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
          {renderedVirtualRows.map(virtualRow => {
            const video = getRow(virtualRow.index);

            // Safety check - video should always exist but guard against edge cases
            if (!video) return null;

            const selected = isSelected(video.mediaId);

            return (
              <MediaGridRow
                key={virtualRow.key}
                video={video}
                index={virtualRow.index}
                isSelected={selected}
                processingPercent={video.isReady
                  ? undefined
                  : JobProgressUtils.percentForStatus(processingProgress?.get(video.mediaId), video.status)}
                onSelectOnly={selectOnly}
                onToggle={toggle}
                onRangeSelect={handleRangeSelect}
                onMenuOpen={handleMenuOpen}
                onNavigate={handleNavigate}
                onPrefetch={onPrefetchDetail}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Single shared action menu — positioned at clicked row's kebab button */}
      <Menu
        opened={menuState.opened}
        onChange={(opened) => { if (!opened) handleMenuClose(); }}
        shadow="md"
        width={180}
        position="bottom-end"
      >
        <Menu.Target>
          <div ref={anchorRef} style={{ position: 'fixed', pointerEvents: 'none', width: 0, height: 0 }} />
        </Menu.Target>
        <Menu.Dropdown>
          {activeVideo && (
            <>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                component={Link}
                to="/videos/$id"
                // @ts-expect-error Mantine polymorphic component loses TanStack Router params inference
                params={{ id: activeVideo.displayId }}
                onClick={handleMenuClose}
              >
                Edit
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCode size={14} />}
                onClick={() => { handleMenuClose(); onPublish?.(activeVideo); }}
              >
                Publish
              </Menu.Item>
              <Menu.Item
                leftSection={<IconPlaylistAdd size={14} />}
                onClick={() => { handleMenuClose(); onAddToPlaylist?.(activeVideo); }}
              >
                Add to Playlist
              </Menu.Item>
              {activeVideo.downloadUrl && (
                <Menu.Item
                  leftSection={<IconDownload size={14} />}
                  component="a"
                  href={activeVideo.downloadUrl}
                  target="_blank"
                  // No "noreferrer": the CDN /link/http download endpoint authorizes
                  // dashboard-originated downloads by Referer (legacy anchors sent it).
                  rel="noopener"
                >
                  Download
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item
                leftSection={activeVideo.isActive
                  ? <IconPlayerPause size={14} />
                  : <IconPlayerPlay size={14} />}
                onClick={() => { handleMenuClose(); onActivateToggle?.(activeVideo); }}
              >
                {activeVideo.isActive ? 'Deactivate' : 'Activate'}
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={() => { handleMenuClose(); handleRowDelete(activeVideo); }}
              >
                Delete
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}
